// core
import React, { ReactElement } from "react";

// components
import LModal from "../../../../common/components/Modals/LModal/LModal";
import ModalHeader from "../../../../common/components/Modals/ModalHeader/ModalHeader";
import styles from "./FinancialStrengthModal.module.scss";
import TableContainer from "../../TableList/TableContainer";
import { IFinancialStrengthRating } from "../../../interfaces/IFinancialStrengthRating";
import InsurerLogo from "../../../../common/components/InsurerLogo/InsurerLogo";
import LAccordion from "../../LAccordion/LAccordion";
import useAccordion from "../../../../common/hooks/useAccordion";
import TableSection from "../../TableList/TableSection/TableSection";
import TableRow from "../../TableList/TableRow/TableRow";
import TableCell from "../../TableList/TableCell/TableCell";
import useWindowDimensions from "../../../../common/hooks/useWindowDimensions";

type ProviderNames = "A.M Best Company" | "Fitch Rating" | "Standard & Poors";

type TableRow = {
    label: string;
    description: string;
};

export type TableSectionType = {
    title?: string;
    items: TableRow[];
};

type FinancialTables = {
    title: ProviderNames;
    rows: TableSectionType[];
    footnote?: string;
};

const tables: FinancialTables[] = [
    {
        title: "A.M Best Company",
        rows: [
            {
                title: "Secure",
                items: [
                    {
                        label: "A++, A+",
                        description: "Superior"
                    },
                    {
                        label: "A, A-",
                        description: "Excellent"
                    },
                    {
                        label: "B++, B+",
                        description: "Good"
                    }
                ]
            },
            {
                title: "Vulnerable",
                items: [
                    {
                        label: "B, B-",
                        description: "Fair"
                    },
                    {
                        label: "C++",
                        description: "Marginal"
                    },
                    {
                        label: "C, C-",
                        description: "Weak"
                    },
                    {
                        label: "D",
                        description: "Poor"
                    },
                    {
                        label: "E",
                        description: "Under regulatory"
                    },
                    {
                        label: "F",
                        description: "In Liquidation"
                    },
                    {
                        label: "S",
                        description: "Suspended"
                    }
                ]
            }
        ]
    },
    {
        title: "Standard & Poors",
        rows: [
            {
                items: [
                    {
                        label: "AAA",
                        description: "Extremely Strong"
                    },
                    {
                        label: "AA",
                        description: "Very Strong"
                    },
                    {
                        label: "BBB",
                        description: "Strong"
                    },
                    {
                        label: "BB",
                        description: "Good"
                    },
                    {
                        label: "B",
                        description: "Marginal"
                    },
                    {
                        label: "CCC",
                        description: "Weak"
                    },
                    {
                        label: "CC",
                        description: "Very Weak"
                    },
                    {
                        label: "R",
                        description: "Regulatory action"
                    }
                ]
            }
        ],
        footnote:
            "Plus (+) or minus (-): The rating from 'AA' to 'CCC' may be modified by the addition of a plus or minus sign to show relative standings within the major rating categories."
    },
    {
        title: "Fitch Rating",
        rows: [
            {
                title: "Secure",
                items: [
                    {
                        label: "AAA",
                        description: "Exceptionally strong"
                    },
                    {
                        label: "AA",
                        description: "Very Strong"
                    },
                    {
                        label: "A",
                        description: "Strong"
                    },
                    {
                        label: "BBB",
                        description: "Good"
                    }
                ]
            },
            {
                title: "Vulnerable",
                items: [
                    {
                        label: "BB",
                        description: "Moderately weak"
                    },

                    {
                        label: "B",
                        description: "Weak"
                    },
                    {
                        label: "CCC",
                        description: "Very Weak"
                    },
                    {
                        label: "CC",
                        description: "Extremely Weak"
                    },
                    {
                        label: "C",
                        description: "Distressed"
                    }
                ]
            }
        ],
        footnote:
            'Note: "+" or "-" may be appended to a rating to indicate the relative position of a credit within the rating category. Such suffixes are not added to ratings in the AAA category or to ratings below the CCC category'
    }
];

interface IFinancialStrengthModal {
    providerId: number;
    providerName: string;
    providerRating: IFinancialStrengthRating;
    isOpen: boolean;
    onClose: () => void;
}

const FinancialStrengthModal: React.FC<IFinancialStrengthModal> = (props) => {
    const { sectionIsOpen, handleChangeSection } = useAccordion<ProviderNames>(
        undefined,
        false
    );
    const { providerId, providerName, providerRating, isOpen, onClose } = props;

    const { isTabletPortrait } = useWindowDimensions();

    return (
        <React.Fragment>
            <LModal
                Header={() => (
                    <ModalHeader onClose={onClose} title="Financial strength rating" />
                )}
                calcHeightMode="auto"
                width="wide"
                isOpen={isOpen}
                onClose={onClose}>
                {() => (
                    <React.Fragment>
                        <div
                            className={
                                styles["financial-strength--provider-container__header"]
                            }>
                            <InsurerLogo
                                includesAboutInsurerModal
                                providerId={providerId}
                            />
                            <h2
                                className={
                                    styles[
                                        "financial-strength--provider-container__header-rating"
                                    ]
                                }>
                                {`${providerRating.rating} (${providerRating.ratingDescription})`}
                            </h2>
                        </div>
                        <div className="gap-xl text-font-size-ml">
                            <span>
                                {`${providerName} has a${
                                    /^[aeiou]/i.test(providerRating.rating) ? "n" : ""
                                } `}
                            </span>
                            <strong>
                                {`${providerRating.rating} (${providerRating.ratingDescription})`}
                            </strong>
                            <span>
                                {` financial strength rating given by ${providerRating.ratingAgency}`}
                            </span>
                        </div>

                        <p className="gap-xl">
                            The insurers on LifeDirect are considered financially stable
                            and have independent financial strength ratings (from either
                            A.M. Best Company, Standard &amp; Poors or Fitch Ratings).
                            Here are the rating scales:
                        </p>
                        {tables.map((box) => (
                            <LAccordion
                                key={box.title}
                                name={box.title}
                                expanded={sectionIsOpen(box.title)}
                                handleChange={handleChangeSection}
                                summary={(): ReactElement => (
                                    <React.Fragment>{box.title}</React.Fragment>
                                )}
                                size="small">
                                <TableContainer footnote={box.footnote}>
                                    {box.rows.map((section) => {
                                        return (
                                            <TableSection
                                                title={section.title}
                                                key={`Section-${section.title}`}>
                                                {section.items.map((row) => {
                                                    return (
                                                        <TableRow key={row.label}>
                                                            <TableCell
                                                                size={
                                                                    isTabletPortrait
                                                                        ? "small"
                                                                        : "large"
                                                                }>
                                                                {row.label}
                                                            </TableCell>
                                                            <TableCell size="medium">
                                                                {row.description}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableSection>
                                        );
                                    })}
                                </TableContainer>
                            </LAccordion>
                        ))}
                    </React.Fragment>
                )}
            </LModal>
        </React.Fragment>
    );
};

export default FinancialStrengthModal;
